import { useState, useRef } from "react";
import { Check, ArrowRight, ArrowLeft, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bangladesh",
  "Belgium", "Brazil", "Cambodia", "Cameroon", "Canada", "Chile", "China", "Colombia",
  "Denmark", "Egypt", "Ethiopia", "Finland", "France", "Germany", "Ghana", "Greece",
  "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Japan", "Jordan",
  "Kenya", "Lebanon", "Malaysia", "Mexico", "Morocco", "Nepal", "Netherlands", "New Zealand",
  "Nigeria", "Norway", "Pakistan", "Palestine", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
  "Romania", "Russia", "Saudi Arabia", "Singapore", "South Africa", "South Korea", "Spain",
  "Sri Lanka", "Sudan", "Sweden", "Switzerland", "Syria", "Taiwan", "Tanzania", "Thailand", "Tunisia",
  "Turkey", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

export default function ApplyPage() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    full_name: "", gender: "", email: "", phone: "",
    nationality: "", city: "", photo_path: "", social_link: ""
  });
  const [errors, setErrors] = useState({});

  const validate = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.full_name.trim()) e.full_name = "Required";
      if (!form.gender) e.gender = "Required";
      if (!form.email.trim()) e.email = "Required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
      if (!form.phone.trim() || form.phone.trim().length < 4) e.phone = "Required";
    } else if (s === 2) {
      if (!form.nationality) e.nationality = "Required";
      if (!form.city.trim()) e.city = "Required";
    } else if (s === 3) {
      if (!form.photo_path) e.photo = "Please upload a personal photo";
      if (!form.social_link.trim() || form.social_link.length < 4) e.social_link = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => validate(step) && setStep(s => Math.min(s + 1, 3));
  const prev = () => setStep(s => Math.max(s - 1, 1));

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Image files only"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/uploads/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("photo_path", res.data.path);
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!validate(3)) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/ambassadors`, form);
      setSubmitted(true);
      toast.success("Application submitted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: undefined }));
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-paper" data-testid="apply-success">
        <div className="max-w-md text-center px-6">
          <div className="h-16 w-16 border border-black/10 flex items-center justify-center mx-auto mb-6">
            <Check className="h-8 w-8 text-crimson" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-3xl text-ink mb-4">Application Received</h1>
          <p className="text-muted mb-8">
            Thank you for applying to become a GYP Ambassador. Our team will review your application and contact you.
          </p>
          <button
            onClick={() => window.location.href = "/"}
            className="bg-ink text-white px-8 py-3 text-xs tracking-[0.2em] uppercase"
            data-testid="return-home-btn"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  const inputCls = (err) =>
    `w-full border p-4 bg-transparent focus:outline-none focus:border-ink ${err ? 'border-crimson' : 'border-black/15'}`;

  return (
    <div className="min-h-[80vh] bg-paper py-16" data-testid="apply-page">
      <div className="max-w-2xl mx-auto px-6">

        {/* Header */}
        <div className="mb-12">
          <p className="text-[10px] tracking-[0.3em] uppercase text-muted mb-3">Ambassador Application</p>
          <h1 className="font-serif text-4xl text-ink">Become a GYP Ambassador</h1>
          <p className="text-muted mt-3 text-sm">Represent the organization in your country and city.</p>
        </div>

        {/* Progress */}
        <div className="flex items-center mb-12">
          {[1, 2, 3].map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={`h-10 w-10 border flex items-center justify-center text-sm ${
                  step >= s ? 'bg-ink text-white border-ink' : 'border-black/20 text-muted'
                }`}
                data-testid={`step-indicator-${s}`}
              >
                {step > s ? <Check className="h-4 w-4" /> : s}
              </div>
              {i < 2 && <div className={`w-16 h-px ${step > s ? 'bg-ink' : 'bg-black/20'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white border border-black/10 p-8 lg:p-12">

          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1-form">
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Full Name *</label>
                <input
                  value={form.full_name}
                  onChange={e => update("full_name", e.target.value)}
                  className={inputCls(errors.full_name)}
                  placeholder="Your full name"
                  data-testid="input-full-name"
                />
                {errors.full_name && <p className="text-crimson text-xs mt-1">{errors.full_name}</p>}
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Gender *</label>
                <select
                  value={form.gender}
                  onChange={e => update("gender", e.target.value)}
                  className={inputCls(errors.gender)}
                  data-testid="select-gender"
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                {errors.gender && <p className="text-crimson text-xs mt-1">{errors.gender}</p>}
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => update("email", e.target.value)}
                  className={inputCls(errors.email)}
                  placeholder="your@email.com"
                  data-testid="input-email"
                />
                {errors.email && <p className="text-crimson text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => update("phone", e.target.value)}
                  className={inputCls(errors.phone)}
                  placeholder="+1 234 567 890"
                  data-testid="input-phone"
                />
                {errors.phone && <p className="text-crimson text-xs mt-1">{errors.phone}</p>}
              </div>
            </div>
          )}

          {/* Step 2: Location */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2-form">
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Nationality *</label>
                <select
                  value={form.nationality}
                  onChange={e => update("nationality", e.target.value)}
                  className={inputCls(errors.nationality)}
                  data-testid="select-nationality"
                >
                  <option value="">Select your nationality</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.nationality && <p className="text-crimson text-xs mt-1">{errors.nationality}</p>}
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">City *</label>
                <input
                  value={form.city}
                  onChange={e => update("city", e.target.value)}
                  className={inputCls(errors.city)}
                  placeholder="Your city"
                  data-testid="input-city"
                />
                {errors.city && <p className="text-crimson text-xs mt-1">{errors.city}</p>}
              </div>
            </div>
          )}

          {/* Step 3: Photo + Social */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-3-form">
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Personal Photo *</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => handleFile(e.target.files?.[0])}
                  data-testid="input-photo"
                />
                {form.photo_path ? (
                  <div className="border border-black/15 p-4 flex items-center gap-4">
                    {photoPreview && <img src={photoPreview} alt="preview" className="h-20 w-20 object-cover" />}
                    <div className="flex-1">
                      <p className="text-sm text-ink">Photo uploaded</p>
                      <button
                        type="button"
                        onClick={() => { update("photo_path", ""); setPhotoPreview(""); }}
                        className="text-xs text-crimson flex items-center gap-1 mt-1"
                        data-testid="remove-photo-btn"
                      >
                        <X className="h-3 w-3" /> Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className={`w-full border border-dashed p-8 flex flex-col items-center gap-2 text-muted hover:border-ink hover:text-ink disabled:opacity-60 ${errors.photo ? 'border-crimson' : 'border-black/25'}`}
                    data-testid="upload-photo-btn"
                  >
                    {uploading ? (
                      <><Loader2 className="h-6 w-6 animate-spin" strokeWidth={1.5} /><span className="text-xs tracking-[0.2em] uppercase">Uploading...</span></>
                    ) : (
                      <><Upload className="h-6 w-6" strokeWidth={1.5} /><span className="text-xs tracking-[0.2em] uppercase">Upload Photo</span></>
                    )}
                  </button>
                )}
                {errors.photo && <p className="text-crimson text-xs mt-1">{errors.photo}</p>}
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Social Media Link *</label>
                <input
                  value={form.social_link}
                  onChange={e => update("social_link", e.target.value)}
                  className={inputCls(errors.social_link)}
                  placeholder="https://instagram.com/your-handle"
                  data-testid="input-social"
                />
                {errors.social_link && <p className="text-crimson text-xs mt-1">{errors.social_link}</p>}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-10 pt-6 border-t border-black/10">
            {step > 1 ? (
              <button onClick={prev} className="flex items-center gap-2 text-sm text-muted hover:text-ink" data-testid="prev-btn">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : <div />}

            {step < 3 ? (
              <button
                onClick={next}
                className="bg-ink text-white px-8 py-3 text-xs tracking-[0.2em] uppercase flex items-center gap-2"
                data-testid="next-btn"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                className="bg-crimson hover:bg-[#6B130B] text-white px-8 py-3 text-xs tracking-[0.2em] uppercase disabled:opacity-50"
                data-testid="submit-btn"
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
